import { storage } from './storage';

const CACHE_KEY = 'cachedLocation';
const CACHE_TTL_MS = 2 * 60 * 1000;
const GEO_TIMEOUT_MS = 8000;

export function getCachedLocation() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const latitude = Number(parsed?.latitude);
    const longitude = Number(parsed?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (Date.now() - (parsed.cachedAt ?? 0) > CACHE_TTL_MS) return null;
    return {
      ...parsed,
      latitude,
      longitude,
    };
  } catch {
    return null;
  }
}

export function clearLocationCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

function cacheLocation(coords) {
  localStorage.setItem(
    CACHE_KEY,
    JSON.stringify({ ...coords, cachedAt: Date.now() }),
  );
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('location_timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('geolocation_unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          address: `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`,
        };
        cacheLocation(coords);
        resolve(coords);
      },
      reject,
      { enableHighAccuracy: false, timeout: GEO_TIMEOUT_MS, maximumAge: 60_000 },
    );
  });
}

async function readStoredUserLocation() {
  try {
    const userRaw = await storage.getItem('user');
    if (!userRaw) return null;
    const user = JSON.parse(userRaw);
    const latitude = Number(user.latitude);
    const longitude = Number(user.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return {
      latitude,
      longitude,
      address: user.address ?? `${latitude}, ${longitude}`,
    };
  } catch {
    return null;
  }
}

export async function resolveStoredOrCurrentLocation({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const cached = getCachedLocation();
    if (cached) return cached;
  } else {
    clearLocationCache();
  }

  const stored = await readStoredUserLocation();
  if (stored) {
    cacheLocation(stored);
    return stored;
  }

  try {
    return await withTimeout(getCurrentPosition(), GEO_TIMEOUT_MS + 500);
  } catch {
    return null;
  }
}

export async function pickLocation() {
  const coords = await getCurrentPosition();
  return coords;
}
